/**
 * @module app.shared
 */

import { Binding } from 'aurelia-binding';

/**
 * Binding behavior that makes it easy to conditionally toggle an event from propagating without requiring a view model function
 *
 * The **CSS** property `pointer-events: all` does effectively the same thing, but support on **HTML** elements
 * is still technically `experimental` and future behavior may change.  Also, while most browsers support it, it's
 * not 100%.
 *
 * ```html
 * <template bindable="disabled" click.trigger="disabled & toggleEvent">
 * ````
 */
export class ToggleEventBindingBehavior {

    public bind(binding: Binding, source: any): void {
        // check for call source, source, and sourceExpression
        if(binding.callSource && binding.source && binding.sourceExpression) {
            // keep reference to sourceExpression
            const expression = binding.sourceExpression;
            // cast binding to any so we can stash stuff on it
            const bindingAny: any = binding;
            // keep reference to original callSource
            bindingAny._originalCallSource = binding.callSource;
            if(bindingAny._originalCallSource) {
                // attach new callSource
                binding.callSource = function(event: Event) {
                    // evaluate expression passed into binding behavior
                    const condition = expression.evaluate(binding.source);
                    if(condition) {
                        // if true, stop event propogation
                        event.stopImmediatePropagation();
                    }
                    // call original callSource with event
                    bindingAny._originalCallSource(event);
                };
            }
        }
    }

    public unbind(binding: Binding, source: any): void {
        const bindingAny: any = binding;
        // check for reference to original callSource
        if(bindingAny._originalCallSource) {
            // restore it
            binding.callSource = bindingAny._originalCallSource;
            // delete stashed reference
            delete bindingAny._originalCallSource;
        }
    }

}
